<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import { flip } from 'svelte/animate';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	// Stores
	import MeetingStore from '@/Stores/Racing/MeetingStore.js';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	// Partials
	import ReorderMeetingObject from '@/Partials/Racing/Meeting/_ReorderMeetingObject.svelte';
	// Services
	import { notify } from '@/Services/Notify';

	dayjs.extend(localizedFormat);

	export let open = false;
	export let selectedDate;

	let formattedMeetings = [];

	const getMeetings = (reset = false) => {
		if (reset) {
			formattedMeetings = [];
		}

		formattedMeetings = $MeetingStore.all().sort(function (a, b) {
			return a.order - b.order || a.name.localeCompare(b.name);
		});

		return formattedMeetings;
	};
	const sortMeetingsByOrder = () => {
		meetings = meetings.sort(function (a, b) {
			return a.order - b.order || a.name.localeCompare(b.name);
		});
		meetings = meetings;
	};

	let meetings = getMeetings(true);

	const reorderMeetingOrder = (meetingList) => {
		let count = 0;

		for (let i = 0; i < meetingList.length; i++) {
			meetingList[i].order = count;
			count++;
		}
		return meetingList;
	};

	let loading = false;
	let hovering = false;

	const drop = (event, target) => {
		event.dataTransfer.dropEffect = 'move';
		const start = parseInt(event.dataTransfer.getData('text/plain'));

		if (start < target) {
			meetings.splice(target + 1, 0, meetings[start]);
			meetings.splice(start, 1);
		} else {
			meetings.splice(target, 0, meetings[start]);
			meetings.splice(start + 1, 1);
		}

		meetings = reorderMeetingOrder(meetings);
		hovering = null;
	};

	const dragstart = (event, i) => {
		event.dataTransfer.effectAllowed = 'move';
		event.dataTransfer.dropEffect = 'move';
		event.dataTransfer.setData('text/plain', i);
	};

	const swap = (arr, index1, index2) =>
		arr.map((val, idx) => {
			if (idx === index1) return arr[index2];
			if (idx === index2) return arr[index1];
			return val;
		});

	const shiftUp = (index) => {
		meetings = swap(meetings, index, index - 1);
		meetings = reorderMeetingOrder(meetings);
	};

	const shiftDown = (index) => {
		meetings = swap(meetings, index, index + 1);
		meetings = reorderMeetingOrder(meetings);
	};

	const saveMeetingsOrder = async () => {
		loading = true;
		let saveOrderOperations = [];
		for (const meeting of meetings) {
			const promise = new Promise((resolve, reject) => {
				MeetingStore.saveMeetingOrder(meeting.id, meeting.order)
					.then(() => {
						resolve();
					})
					.catch((error) => {
						reject(error);
					});
			});
			saveOrderOperations.push(promise);
		}
		await Promise.all(saveOrderOperations)
			.then(() => {
				notify({
					type: 'success',
					title: 'Meeting updated',
					message: 'Meeting order successfully updated',
					timeout: 3
				});
			})
			.catch((e) => {
				console.error(e);
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update meeting order',
					timeout: 5
				});
			})
			.finally(() => {
				open = false;
			});
	};
</script>

{#if open}
	<Modal on:close={() => (open = false)} className="mx-2 md:w-1/3 md:mx-auto">
		<!-- HEADER -->
		<div class="pt-2 font-semibold text-xl text-center uppercase mb-4" slot="header">
			Reorder Meetings for {selectedDate}
		</div>
		<!-- /HEADER -->
		<div class="list flex flex-col justify-center items-center my-3">
			{#each meetings as meeting, index (meeting.id)}
				<div
					class="list-item w-full"
					animate:flip={{ duration: 200 }}
					draggable={true}
					on:dragstart={(event) => dragstart(event, index)}
					on:drop|preventDefault={(event) => drop(event, index)}
					ondragover="return false"
					on:dragenter={() => (hovering = index)}
					class:is-active={hovering === index}
				>
					<svelte:component
						this={ReorderMeetingObject}
						bind:meeting
						order={index}
						last={meetings.length - 1}
						active={hovering === index}
						on:up={() => shiftUp(index)}
						on:down={() => shiftDown(index)}
						on:sort={() => sortMeetingsByOrder()}
					/>
				</div>
			{/each}
		</div>

		<!-- CONTROLS -->
		<div class="flex flex-col w-full px-4 pt-2 text-center">
			<LoadingButton {loading} className="mb-4 btn btn-primary" on:click={() => saveMeetingsOrder()}>
				Save order
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}> Cancel</button>
		</div>
		<!-- /CONTROLS -->
	</Modal>
{/if}

<style>
	.list {
		background-color: white;
		border-radius: 4px;
		box-shadow:
			0 2px 3px rgba(10, 10, 10, 0.1),
			0 0 0 1px rgba(10, 10, 10, 0.1);
	}

	.list-item {
		display: block;
		padding: 0.1em 0.5em;
	}

	.list-item.is-active {
		background-color: #3273dc;
		color: #fff;
	}
</style>
